import React, { useEffect, useRef, useState } from 'react'
import { defaultCorners, loadImage } from '../lib/cv'

// Manual crop adjustment: drag the 4 corners over the original image.
export default function CropEditor({ page, onSave, onCancel }) {
  const containerRef = useRef(null)
  const [img, setImg] = useState(null)
  const [corners, setCorners] = useState(null)
  const dragRef = useRef(null) // index of corner being dragged

  useEffect(() => {
    let alive = true
    loadImage(page.originalDataUrl).then((im) => {
      if (!alive) return
      setImg(im)
      setCorners(page.corners || defaultCorners(im.width, im.height))
    })
    return () => {
      alive = false
    }
  }, [page])

  if (!img || !corners) return <div className="loading">Loading…</div>

  // Map between displayed size and natural image coords
  const toImageCoords = (clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect()
    const sx = img.width / rect.width
    const sy = img.height / rect.height
    return {
      x: Math.max(0, Math.min(img.width, (clientX - rect.left) * sx)),
      y: Math.max(0, Math.min(img.height, (clientY - rect.top) * sy)),
    }
  }

  const onPointerDown = (i) => (e) => {
    e.target.setPointerCapture(e.pointerId)
    dragRef.current = i
  }
  const onPointerMove = (e) => {
    if (dragRef.current === null || dragRef.current === undefined) return
    const p = toImageCoords(e.clientX, e.clientY)
    setCorners((cs) => cs.map((c, i) => (i === dragRef.current ? p : c)))
  }
  const onPointerUp = () => {
    dragRef.current = null
  }

  const pct = (p) => ({ left: `${(p.x / img.width) * 100}%`, top: `${(p.y / img.height) * 100}%` })
  const points = corners.map((p) => `${(p.x / img.width) * 100},${(p.y / img.height) * 100}`).join(' ')

  return (
    <div className="crop-editor">
      <div
        className="crop-stage"
        ref={containerRef}
        style={{ aspectRatio: `${img.width} / ${img.height}` }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <img src={page.originalDataUrl} alt="Original scan" draggable={false} />
        <svg className="crop-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polygon points={points} />
        </svg>
        {corners.map((p, i) => (
          <div key={i} className="handle" style={pct(p)} onPointerDown={onPointerDown(i)} />
        ))}
      </div>
      <div className="controls">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button
          className="btn"
          onClick={() => setCorners(defaultCorners(img.width, img.height, 0))}
          title="Use the full image"
        >
          Full image
        </button>
        <button className="btn btn-primary" onClick={() => onSave(corners)}>Done</button>
      </div>
    </div>
  )
}
